/**
 * ParlayCard Component
 * Compact tracked parlay card with theme support, extracted from TrackedParlaysPanel
 */

import ConfirmModal from '@components/ui/ConfirmModal';
import LegProgressBar from '@components/ui/LegProgressBar';
import SportsbookBadge from '@components/ui/SportsbookBadge';
import { TierBadge as FeatureTierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { ScoringResult } from '@hooks/useStrategyStats';
import type { TrackedParlay, TrackedParlayLeg } from '@hooks/useTrackedParlays';
import { canDeleteParlay } from '@parlay-picker/shared/deletionGuards';
import { isLegEffectivelyDnp } from '@parlay-picker/shared/dnpUtils';
import { formatTrackedDate } from '@parlay-picker/shared/formatting';
import type { LiveGameScores, LiveLegStats } from '@parlay-picker/shared/hooks';
import { getEffectiveStatType } from '@parlay-picker/shared/statType';
import { getTeamLogo } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { Share2, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  LiveBadge,
  OriginBadge,
  ParlayResultBadge,
  ScoreBadge,
  SgpBadge,
  TierBadge,
} from './ParlayBadges';
import { ScoreComparisonPanel } from './ScoreComparisonPanel';
import {
  calculateEffectiveOdds,
  countDnpLegs,
  formatMatchupPrefix,
  formatTrackedLegProp,
  isProgressBarEligible,
  isTrackedLegGameLevel,
  isTrackedLegTotal,
} from './utils';

// =============================================================================
// Types
// =============================================================================

export interface ParlayCardProps {
  parlay: TrackedParlay;
  isConnected: boolean;
  liveGamesCount: number;
  onClick: () => void;
  onRemove?: (id: number) => void;
  onShare?: (parlay: TrackedParlay) => void;
  getPlayerLiveStats: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
    statType: string,
  ) => LiveLegStats | null;
  getGameScores: (gameId: string | null) => LiveGameScores | null;
  isGameLive: (gameId: string | null) => boolean;
  isGameFinal: (gameId: string | null) => boolean;
  hasLiveData: (gameId: string | null) => boolean;
  canRemoveLeg: (gameId: string | null) => boolean;
  isPlayerDNP: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
  ) => boolean | null;
  isExpanded?: boolean;
  showComparison?: boolean;
  currentScores?: ScoringResult | null;
}

// =============================================================================
// Component
// =============================================================================

export const ParlayCard = React.memo(function ParlayCard({
  parlay,
  isConnected,
  liveGamesCount,
  onClick,
  onRemove,
  onShare,
  getPlayerLiveStats,
  getGameScores,
  isGameLive,
  isGameFinal,
  hasLiveData: _hasLiveData,
  canRemoveLeg: _canRemoveLeg,
  isPlayerDNP,
  isExpanded,
  showComparison,
  currentScores,
}: ParlayCardProps): React.ReactElement {
  const { isLocked: isFeatureLocked } = useFeatureAccess();
  const shareLocked = isFeatureLocked('social_share');
  const [showShareUpgrade, setShowShareUpgrade] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const effectiveOdds = useMemo(
    () => calculateEffectiveOdds(parlay, isPlayerDNP),
    [parlay, isPlayerDNP],
  );

  const dnpCount = useMemo(() => countDnpLegs(parlay, isPlayerDNP), [parlay, isPlayerDNP]);

  // Prefer client-calculated effectiveOdds (handles old settled parlays where finalOdds == totalOdds)
  const displayOdds = effectiveOdds || parlay.finalOdds;
  const showOddsChange = displayOdds && displayOdds !== parlay.totalOdds;
  const hasLiveGame =
    parlay.result === 'pending' &&
    isConnected &&
    parlay.legs.some((leg) => isGameLive(leg.gameId ?? null));

  // Card border + background based on result
  const cardClasses = cn(
    'rounded-lg p-3 border-l-4 cursor-pointer transition-all',
    parlay.result === 'won'
      ? 'bg-theme-success/10 border-theme-success'
      : parlay.result === 'lost'
        ? 'bg-theme-danger/10 border-theme-danger'
        : parlay.result === 'void'
          ? 'bg-purple-500/10 border-purple-500'
          : 'bg-theme-surface border-theme-primary',
    isExpanded ? 'ring-2 ring-theme-primary/50' : 'hover:ring-1 hover:ring-theme-primary/30',
  );

  return (
    <div data-testid="tracked-parlay-card" onClick={onClick} className={cardClasses}>
      {/* Header: badges + odds */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <TierBadge tier={parlay.tier || 'Custom'} />
            <ParlayResultBadge result={parlay.result} />
            {parlay.origins.map((origin) => (
              <OriginBadge key={origin} origin={origin} />
            ))}
            {parlay.isSgp && <SgpBadge />}
            {parlay.sportsbook && <SportsbookBadge sportsbook={parlay.sportsbook} size="sm" />}
            {hasLiveGame && <LiveBadge liveGamesCount={liveGamesCount} />}
            <ScoreBadge
              type="user"
              score={parlay.trackedStrategyScore}
              isLabelTrusted={parlay.trackedLabelTrusted}
            />
            <ScoreBadge
              type="ai"
              score={parlay.trackedPredictionScore}
              isLabelTrusted={parlay.trackedLabelTrusted}
            />
          </div>
          <div className="text-xs mt-1 text-theme-text-secondary">
            {formatTrackedDate(parlay.createdAt)} · {parlay.confidence} Confidence
          </div>
        </div>
        <div className="text-right">
          {showOddsChange ? (
            <div className="flex items-center gap-2">
              <span className="text-xs line-through text-theme-text-tertiary">
                {parlay.totalOdds}
              </span>
              <span
                className={cn(
                  'font-bold',
                  parlay.result === 'won'
                    ? 'text-theme-success'
                    : parlay.result === 'lost'
                      ? 'text-theme-danger'
                      : parlay.result === 'void' || parlay.result === 'push'
                        ? 'text-theme-warning'
                        : 'text-theme-text-secondary',
                )}
                title={`${dnpCount} leg${dnpCount !== 1 ? 's' : ''} voided (DNP)`}
              >
                {displayOdds}
              </span>
            </div>
          ) : (
            <div
              className={cn(
                'font-bold',
                parlay.result === 'won'
                  ? 'text-theme-success'
                  : parlay.result === 'lost'
                    ? 'text-theme-danger'
                    : parlay.result === 'void' || parlay.result === 'push'
                      ? 'text-theme-warning'
                      : 'text-theme-text-secondary',
              )}
            >
              {parlay.totalOdds}
            </div>
          )}
          <div className="text-xs text-theme-text-secondary">
            {parlay.legs.length} legs{dnpCount > 0 && ` (${dnpCount} DNP)`}
          </div>
        </div>
      </div>

      {/* Legs — scrollable when more than 5 */}
      <div
        className={cn('space-y-2 mb-3', parlay.legs.length > 5 && 'max-h-60 overflow-y-auto pr-1')}
      >
        {parlay.legs.map((leg, j) => (
          <LegRow
            key={j}
            leg={leg}
            isConnected={isConnected}
            getPlayerLiveStats={getPlayerLiveStats}
            getGameScores={getGameScores}
            isGameFinal={isGameFinal}
            isPlayerDNP={isPlayerDNP}
          />
        ))}
      </div>

      {/* Score comparison panel — show even without current scores (locked scores come from parlay) */}
      {showComparison && (
        <ScoreComparisonPanel
          parlay={parlay}
          strategyScore={currentScores?.strategyScore ?? null}
          predictionScore={currentScores?.predictionScore ?? null}
        />
      )}

      {/* Void message */}
      {parlay.result === 'void' && (
        <div className="text-xs italic text-purple-500">
          {parlay.isSgp ? 'SGP voided - player DNP' : 'Voided'}
        </div>
      )}

      {/* Actions footer */}
      <div className="flex items-center justify-end mt-2">
        <div className="flex items-center gap-2">
          {onShare &&
            (shareLocked ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareUpgrade(true);
                }}
                className="px-2.5 py-1 rounded text-xs transition flex items-center gap-1.5 bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-active"
                title="Pro feature"
              >
                <Share2 size={13} />
                Share
                <FeatureTierBadge feature="social_share" size="md" />
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(parlay);
                }}
                className="px-2.5 py-1 rounded text-xs transition flex items-center gap-1.5 bg-theme-primary/15 text-theme-primary hover:bg-theme-primary/25"
                title="Share with friends"
              >
                <Share2 size={13} />
                Share
              </button>
            ))}
          {onRemove && canDeleteParlay(parlay) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="px-2.5 py-1 rounded text-xs transition flex items-center gap-1.5 bg-theme-danger/15 text-theme-danger hover:bg-theme-danger/25"
              title="Remove from tracking"
            >
              <Trash2 size={13} />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* The modals render inside the card, so their clicks must not toggle it */}
      <div onClick={(e) => e.stopPropagation()}>
        {showShareUpgrade && (
          <UpgradeModal
            mode="feature"
            feature="social_share"
            onDismiss={() => setShowShareUpgrade(false)}
          />
        )}

        {onRemove && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            title="Delete Parlay"
            message="Are you sure you want to remove this parlay? This action cannot be undone."
            confirmLabel="Delete"
            isDestructive
            onConfirm={() => {
              setShowDeleteConfirm(false);
              onRemove(parlay.id);
            }}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </div>
  );
});

// =============================================================================
// LegRow Sub-component
// =============================================================================

interface LegRowProps {
  leg: TrackedParlayLeg;
  isConnected: boolean;
  getPlayerLiveStats: ParlayCardProps['getPlayerLiveStats'];
  getGameScores: ParlayCardProps['getGameScores'];
  isGameFinal: ParlayCardProps['isGameFinal'];
  isPlayerDNP: ParlayCardProps['isPlayerDNP'];
}

function LegRow({
  leg,
  isConnected,
  getPlayerLiveStats,
  getGameScores,
  isGameFinal,
  isPlayerDNP,
}: LegRowProps): React.ReactElement {
  const isTotal = isTrackedLegTotal(leg);
  // Tracking stores a team bet's team in `player`, so a game leg is a team row regardless
  const playerName = isTrackedLegGameLevel(leg) ? null : leg.player;
  const effectiveStatType = getEffectiveStatType(leg);
  const isSpread =
    effectiveStatType?.toLowerCase() === 'spread' || effectiveStatType?.toLowerCase() === 'spreads';
  const isMoneyline =
    effectiveStatType?.toLowerCase() === 'moneyline' || effectiveStatType?.toLowerCase() === 'h2h';

  const rawLiveStats =
    !isTotal && playerName
      ? getPlayerLiveStats(leg.gameId ?? null, leg.playerId ?? null, playerName, effectiveStatType)
      : null;
  const rawGameScores = isTotal ? getGameScores(leg.gameId ?? null) : null;
  // For settled totals without SSE, synthesize gameScores from DB actual value
  const gameScores: LiveGameScores | null =
    rawGameScores ??
    (isTotal && leg.actual != null
      ? {
          homeScore: leg.actual,
          awayScore: 0,
          quarter: null,
          timeRemaining: null,
          gameStatus: 'final' as const,
        }
      : null);

  const gameFinal = isGameFinal(leg.gameId ?? null);

  // Prefer leg.actual when available — SSE data can be stale or stat type unmapped
  // For settled legs without SSE, synthesize stats from DB actual value
  const liveStats =
    rawLiveStats && leg.actual != null
      ? { ...rawLiveStats, currentStat: leg.actual }
      : (rawLiveStats ??
        (leg.actual != null
          ? {
              currentStat: leg.actual,
              quarter: null,
              timeRemaining: null,
              gameStatus: 'final' as const,
            }
          : null));

  const hasLiveStatsForLeg = isTotal ? gameScores !== null : liveStats !== null;
  const showDnp = isLegEffectivelyDnp(leg, isPlayerDNP);
  const showProgress =
    !showDnp &&
    isProgressBarEligible(leg) &&
    ((isConnected && hasLiveStatsForLeg) || leg.actual != null);
  const showFinalIndicator = !leg.settled && gameFinal && !showProgress && !showDnp;

  const legBgClasses = cn(
    'rounded px-1 py-1',
    leg.hit === true ? 'bg-theme-success/10' : leg.hit === false ? 'bg-theme-danger/10' : '',
  );

  // For spread/ML: detect if the bet is on leg.opponentTeam by checking the prop string, since
  // older legs store the away team in leg.team rather than the team backed.
  const betOnOpponent =
    (isSpread || isMoneyline) &&
    leg.opponentTeam &&
    leg.prop &&
    (() => {
      const propLower = leg.prop!.toLowerCase();
      // Skip totals-style props (over/under)
      if (propLower.includes('over ') || propLower.includes('under ')) return false;
      const oppMascot = leg.opponentTeam!.toLowerCase().split(' ').pop() || '';
      const teamMascot = (leg.team || '').toLowerCase().split(' ').pop() || '';
      return oppMascot && propLower.includes(oppMascot) && !propLower.includes(teamMascot);
    })();

  // Swap primary/badge team when bet is on the opponent
  const primaryTeam = betOnOpponent ? leg.opponentTeam : leg.team;
  const secondaryTeam = betOnOpponent ? leg.team : leg.opponentTeam;

  const [imageError, setImageError] = React.useState(false);
  const leagueKey = leg.leagueKey;
  const headshotUrl = usePlayerHeadshotUrl(!isTotal ? playerName : null, leagueKey);
  const teamLogo = primaryTeam ? getTeamLogo(primaryTeam, leagueKey) : null;
  const opponentLogo = secondaryTeam ? getTeamLogo(secondaryTeam, leagueKey) : null;

  return (
    <div className={legBgClasses}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {/* Player headshot with team badge (for player props) */}
          {!isTotal && playerName && (
            <div className="relative shrink-0">
              {headshotUrl && !imageError ? (
                <img
                  src={headshotUrl}
                  alt={playerName}
                  className="w-7 h-7 rounded-full object-cover bg-theme-surface-hover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-theme-surface-hover flex items-center justify-center text-[10px] font-medium text-theme-text-secondary">
                  {playerName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                </div>
              )}
              {teamLogo && (
                <img
                  src={teamLogo}
                  alt=""
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-theme-surface border border-theme-border"
                />
              )}
              {/* Status dot overlay */}
              <div
                className="absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full border border-theme-surface"
                style={{
                  backgroundColor: showDnp
                    ? '#f59e0b'
                    : leg.hit === true
                      ? '#10b981'
                      : leg.hit === false
                        ? '#ef4444'
                        : '#6b7280',
                }}
              />
            </div>
          )}

          {/* Team logo(s) for game-level props (spreads, moneyline, totals) */}
          {!playerName && teamLogo && (
            <div className="relative shrink-0 flex items-center gap-1">
              <div className="relative">
                <img
                  src={teamLogo}
                  alt={primaryTeam || ''}
                  className="w-7 h-7 rounded-full object-contain bg-theme-surface-hover p-0.5"
                />
                {/* Spreads & ML: opponent logo overlaps like a badge */}
                {opponentLogo && (isSpread || isMoneyline) && (
                  <img
                    src={opponentLogo}
                    alt={secondaryTeam || ''}
                    className="absolute -bottom-0.5 -right-1.5 w-4 h-4 rounded-full object-contain bg-theme-surface border border-theme-border p-px"
                  />
                )}
              </div>
              {/* Totals: opponent logo side by side, same size */}
              {opponentLogo && !isSpread && !isMoneyline && (
                <img
                  src={opponentLogo}
                  alt={secondaryTeam || ''}
                  className="w-7 h-7 rounded-full object-contain bg-theme-surface-hover p-0.5"
                />
              )}
              {/* Status dot overlay */}
              <div
                className="absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full border border-theme-surface"
                style={{
                  backgroundColor: showDnp
                    ? '#f59e0b'
                    : leg.hit === true
                      ? '#10b981'
                      : leg.hit === false
                        ? '#ef4444'
                        : '#6b7280',
                }}
              />
            </div>
          )}
          {/* Fallback status icon when no team logo */}
          {!playerName &&
            !teamLogo &&
            (showDnp ? (
              <span className="text-theme-warning" title="Did Not Play">
                ⊘
              </span>
            ) : leg.hit === true ? (
              <span className="text-theme-success">✓</span>
            ) : leg.hit === false ? (
              <span className="text-theme-danger">✗</span>
            ) : (
              <span className="text-theme-text-tertiary">○</span>
            ))}

          {/* Leg text */}
          <span className={cn('text-theme-text', showDnp && 'italic')}>
            {playerName
              ? `${playerName} - ${formatTrackedLegProp(leg)}`
              : isTrackedLegGameLevel(leg)
                ? `${formatMatchupPrefix(leg)}${formatTrackedLegProp(leg)}`
                : formatTrackedLegProp(leg)}
            {showDnp && <span className="text-xs ml-1">(DNP)</span>}
          </span>

          {showFinalIndicator && (
            <span
              className="text-[9px] px-1 rounded bg-theme-surface-active text-theme-text-secondary"
              title="Game ended - awaiting settlement"
            >
              Final
            </span>
          )}
        </div>
        {showDnp ? (
          <span className="flex items-center gap-1 ml-2 shrink-0">
            <span className="text-theme-text-tertiary text-xs line-through">{leg.odds}</span>
            <span className="text-[10px] font-medium text-theme-warning">VOID</span>
          </span>
        ) : (
          <span className="text-theme-text-tertiary text-xs ml-2 shrink-0">{leg.odds}</span>
        )}
      </div>

      {/* Progress bar */}
      {showProgress && leg.line != null && leg.overUnder && (
        <div className="mt-1 ml-6">
          {isTotal ? (
            <LegProgressBar
              type="total"
              line={leg.line}
              overUnder={leg.overUnder as 'Over' | 'Under'}
              gameScores={gameScores}
              settled={leg.settled}
              hit={leg.hit}
            />
          ) : (
            <LegProgressBar
              type="player"
              line={leg.line}
              overUnder={leg.overUnder as 'Over' | 'Under'}
              statType={effectiveStatType}
              liveStats={liveStats}
              settled={leg.settled}
              hit={leg.hit}
            />
          )}
        </div>
      )}
    </div>
  );
}
